import { AccessLevel, DiscountType, NotificationType } from '@dev/ee-api/ee-api-models';
import { identity, isEmpty } from 'lodash-es';

import { convertToLocalTime } from '@dev/ee-tools';
import dayjs from '@/services/tools/dayjs';
import { getAccountStore } from '@/store/account/actions';
import { getDashboards } from '@/store/products/actions';
import { getNotificationStore } from '@/store/notifications/actions';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useActivePlan } from '../useActivePlan';
import { useCurrentPlanStatus } from '../useCurrentPlanStatus';

type BannerPropsT = {
    expireDay?: string;
    discountDuration?: number;
    discount: number;
    buyButtonVisible?: boolean;
    customText?: string;
};
export interface DiscountInterface {
    discountEnabled: boolean;
    discount: number;
    discountDuration: number;
    discountExpireDay: string;
    isForMultipleMonths: boolean;
    // discountText: string;
    bannerProps: BannerPropsT;
}

const DISCOUNT_BANNER_NAME = 'Promotion discount';

export const useDiscount = (): DiscountInterface => {
    const { t } = useTranslation();
    const { notificationList } = useSelector(getNotificationStore);
    const { GeneralInfo, AdvancedOptions } = useSelector(getAccountStore) || {};
    const dashboards = useSelector(getDashboards);
    const { ActivePlanDetails } = useActivePlan();
    const { isExpired, isTrialOrFree } = useCurrentPlanStatus();
    const activePlanDiscounts = ActivePlanDetails?.Discounts;
    const discountBanner = notificationList?.find(
        (n) => n.Type === NotificationType.Banner && n.Name === DISCOUNT_BANNER_NAME
    );
    const { Content } = discountBanner || {};

    const isCountryBanner = activePlanDiscounts?.DiscountType === DiscountType.Country;
    const accountConditionForHiddenDiscount = {
        users: GeneralInfo?.IsUser && !GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ViewBilling),
        subAccounts: GeneralInfo?.IsSub,
        customBranding: AdvancedOptions?.IsOwnedByReseller,
        accountHasDiscountAlready: dashboards?.some(
            ({ ActivePlanDetails }) =>
                ActivePlanDetails?.Discounts?.DiscountExpiry &&
                !(ActivePlanDetails?.IsTrial || ActivePlanDetails?.IsFree)
        ),
        discountHasNoValue: !(Content?.DiscountPercent || isCountryBanner),
    };

    const backendConditionsToHideDiscount = {
        accHasDiscountSet: !(discountBanner || (isCountryBanner && !isExpired && isTrialOrFree)),
    };

    const hideDiscount = Object.values({
        ...accountConditionForHiddenDiscount,
        ...backendConditionsToHideDiscount,
    })?.some(identity);

    const discount = +(Content?.DiscountPercent || 0) * 100;

    const promoExpiryDateAsUTC = dayjs(Content?.PromoExpiry).utc(true).toString();

    const isForMultipleMonths = dayjs().add(1, 'month').isBefore(dayjs(promoExpiryDateAsUTC));

    // const discountText = isForMultipleMonths
    // ? t('discount_description_text', {
    //       discountPercent: discount,
    //       discountExpireDay: dayjs(Content?.PromoExpiry).utc(true).format(DISCOUNT_DATE_SHORT),
    //   })
    // : t('discount_for_the_first_month', { discountPercent: discount })

    const discountExpireDay = convertToLocalTime(promoExpiryDateAsUTC);
    const discountDuration = +(Content?.DiscountDurationMonths || 0);

    const referralDiscountConfig = {
        expireDay: discountExpireDay,
        discountDuration,
        discount,
        buyButtonVisible: true,
        customText: null,
    };

    const accountDiscountPercent = +(activePlanDiscounts?.DiscountPercent || 0) * 100;
    const countryDiscount = {
        discount: accountDiscountPercent,
        buyButtonVisible: false,
        customText: t('pricing_for_ee_in_region_discounted_by', { percent: accountDiscountPercent }),
    };

    const bannerProps =
        DiscountType.Country === activePlanDiscounts?.DiscountType ? countryDiscount : referralDiscountConfig;

    return {
        discountEnabled: !hideDiscount && !isEmpty(dashboards),
        discount,
        discountDuration,
        discountExpireDay: convertToLocalTime(promoExpiryDateAsUTC),
        isForMultipleMonths,
        bannerProps,
    };
};
